import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kJsonObject } from "../../../Admin/kReusableCoreTS/kJsonObject";
import { AppStanding } from "./AppStanding";
import { AppChatArchiveRules, AppChatSettingValue } from "./AppChatArchiveRules";
import { AppChatProp, AppChatState, AppChatStateDoc, AppContactInfo } from "./AppChatState";

// AppChatSettingsRules - THE RULES OF THE "CHAT SETTINGS" DIALOG (owner, 2026-10-13: "Move the translation settings into
// a new chat settings dialog", "No property should ever be displayed as un-known"). Habitat-free: no DOM, no transport -
// the Bridge suite reads the service's real answers through `RowOf`.
//
// ⛔ NEVER THE WORD. A row has exactly these looks, and none of them is a missing value:
//   LOADING  - no answer yet: a skeleton and "Checking with WhatsApp…" (an in-progress state, with the control disabled);
//   VALUE    - the service's value (`AppChatState`: measured, or WhatsApp's own meaning of absence) - `Checking` adds a
//              quiet hint while the service is still reading from WhatsApp (`resolving`);
//   CHANGING - the value the owner just chose, shown at once, with "Saving…" / "Waiting for WhatsApp to confirm…";
//   and a capability that is not available = a DISABLED control whose reason (`Why`) is the service's own sentence.
// A request that never answers ends in ONE sentence for the whole dialog (`AppChatSettingsModel.Fail`) with "Try
// again" - the rows are then not drawn at all, so no property is shown without a value.
// ⛔ Nothing here sends a message (decision 9): archive, mute, the timer and the star are settings of the chat.
// A second consumer changes the names and option labels; the row arithmetic is the wire's.

/** One setting, ready to draw. */
export class AppChatSettingRow {
    public Setting: string = "";
    public Name: string = "";
    /** The value in words ("Archived", "Muted until Tue 14 Oct, 18:30", "7 days"). "" only while `Loading`. */
    public Words: string = "";
    /** Where the value comes from - a tooltip on the words, never a label. */
    public Source: string = "";
    public Loading: boolean = false;
    /** In-progress words beside the value ("Checking with WhatsApp…", "Saving…"), "" when nothing is in progress. */
    public Hint: string = "";
    public Busy: boolean = false;
    public Disabled: boolean = false;
    /** Why the control is disabled (the service's sentence) - its tooltip and description. */
    public Why: string = "";
    /** A switch's state (archive, star). */
    public On: boolean = false;
    /** The pressed option of a choice (mute, timer) as its value's text; "" = none of the presets. */
    public Choice: string = "";
    /** The service's sentence after a refused or unconfirmed change (ink, beside the control). */
    public Note: string = "";
}

/** The whole dialog's settings part, ready to draw. */
export class AppChatSettingsModel {
    public static readonly Loading: string = "loading";
    public static readonly Ready: string = "ready";
    public static readonly Failed: string = "failed";
    public Phase: string = AppChatSettingsModel.Loading;
    /** `Failed` only: the one sentence shown instead of the rows. */
    public Fail: string = "";
    public CanRetry: boolean = false;
    /** Whole seconds something has been in progress (a quiet counter beside the hint; 0 = none). */
    public Elapsed: number = 0;
    public Rows: AppChatSettingRow[] = [];
    /** ⭐ The contact's details: [field id, label, value] - only fields with a value (`AppChatSettingsRules.InfoRows`). */
    public Info: string[][] = [];
    /** Why the details are not there yet (the service's sentence) - ink under the section; "" = nothing to say. */
    public InfoNote: string = "";
    /** The chat is locked on the phone (WhatsApp's Chat Lock) - said in one line, nothing hidden. */
    public Locked: boolean = false;
}

/** What a write (or a read) answered, beyond the state document. */
export class AppChatSettingsVerdict {
    public Doc: AppChatStateDoc = new AppChatStateDoc();
    /** No answer at all (status 0, a 5xx, not JSON): the outcome is not known. */
    public Lost: boolean = false;
    /** The service took the change and WhatsApp has not confirmed it yet. */
    public Pending: boolean = false;
    public Why: string = "";
    /** A refusal that clears by itself: ask again after this many seconds (0 = none). */
    public RetrySec: number = 0;
}

export class AppChatSettingsRules {
    public static readonly Archive: string = "archive";
    public static readonly Mute: string = "mute";
    public static readonly Disappearing: string = "disappearing";
    public static readonly Star: string = "star";
    /** ⭐ CHAT AND CONTACT ACTIONS (2026-10-14): three phone yes/no's and two actions behind a confirm step. */
    public static readonly Pin: string = "pin";
    public static readonly Read: string = "read";
    public static readonly Block: string = "block";
    public static readonly Clear: string = "clear";
    public static readonly Delete: string = "delete";
    /** The order of the rows. */
    public static readonly All: string[] = ["archive", "mute", "disappearing", "star"];
    /** ⭐ The rows a ONE-TO-ONE chat's dialog offers (2026-10-14): the settings plus the chat and contact actions. */
    public static readonly Direct: string[] = ["archive", "pin", "read", "mute", "disappearing", "block", "clear", "delete", "star"];
    /** What the confirm step says - plainly what goes where. ⭐ (owner 2026-10-22) a chat deleted on the phone is hidden in WACommander too. */
    public static readonly ConfirmWords: Record<string, string> = {
        clear: "Clear this chat in WhatsApp on your phone? WhatsApp on your phone and your linked devices lose these messages. WACommander's archive keeps them.",
        delete: "Delete this chat in WhatsApp on your phone? It leaves your phone's chat list, and WACommander hides it too. A new message brings the chat back.",
    };
    public static readonly ActWords: Record<string, string> = { clear: "Clear chat", delete: "Delete chat" };
    public static readonly MuteOptions: string[][] = [["0", "On"], ["28800", "8 hours"], ["604800", "1 week"], ["-1", "Always"]];
    public static readonly TimerOptions: string[][] = [["0", "Off"], ["86400", "24 hours"], ["604800", "7 days"], ["7776000", "90 days"]];
    public static readonly SavingWords: string = "Saving…";
    public static readonly ConfirmingWords: string = "Waiting for WhatsApp to confirm…";
    public static readonly StarLoadingWords: string = "Checking…";
    public static readonly LostWhy: string = "Couldn't reach WACommander to read this chat's settings. Check your connection.";
    public static readonly SignInWhy: string = "Sign in again to see this chat's settings.";
    public static readonly UnconfirmedWhy: string = "Couldn't confirm that change. Check your connection, then look here again.";
    public static readonly NotAppliedWhy: string = "That change was not applied. Check WhatsApp on your phone.";
    public static readonly RefusedWhy: string = "That change could not be made just now.";
    /** A lost read is asked again after 2, 4, 8, 8, 8 s - then the dialog says so and offers "Try again". */
    public static readonly MaxRetries: number = 5;
    /** `resolving`: asked again after the service's `recheckSec`, at most this many times. */
    public static readonly MaxRechecks: number = 10;
    /** A change awaiting WhatsApp: read again every `ConfirmMs`, at most `MaxConfirms` times. */
    public static readonly ConfirmMs: number = 3000;
    public static readonly MaxConfirms: number = 20;
    public static readonly MaxRetrySec: number = 600;

    /** DOCUMENTED EXCEPTION: pure - 2 s doubling to 8 s. ERROR-SUPPRESSED-JUSTIFIED: arithmetic, cannot throw. */
    public static RetryMs = (Attempt: number): number => {
        if (Attempt <= 1) { return 2000; }
        if (Attempt === 2) { return 4000; }
        return 8000;
    };

    /** DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static IsSwitch = (Setting: string): boolean => {
        return Setting === AppChatSettingsRules.Archive || Setting === AppChatSettingsRules.Star || Setting === AppChatSettingsRules.Pin
            || Setting === AppChatSettingsRules.Read || Setting === AppChatSettingsRules.Block;
    };

    /** DOCUMENTED EXCEPTION: pure lookup - an action behind a confirm step (its value is always `true`). ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static IsAction = (Setting: string): boolean => {
        return Setting === AppChatSettingsRules.Clear || Setting === AppChatSettingsRules.Delete;
    };

    /** DOCUMENTED EXCEPTION: pure lookup of the choices a setting offers. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Options = (Setting: string): string[][] => {
        if (Setting === AppChatSettingsRules.Mute) { return AppChatSettingsRules.MuteOptions; }
        if (Setting === AppChatSettingsRules.Disappearing) { return AppChatSettingsRules.TimerOptions; }
        return [];
    };

    /** The row's name. Shared with the wire's own naming (`AppChatArchiveRules.Name`). */
    public static Name = (Setting: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppChatArchiveRules.Name(Setting);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.Name");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: picks one setting's property off the document. ERROR-SUPPRESSED-JUSTIFIED: field reads. */
    public static PropOf = (Doc: AppChatStateDoc, Setting: string): AppChatProp => {
        if (Setting === AppChatSettingsRules.Mute) { return Doc.Mute; }
        if (Setting === AppChatSettingsRules.Disappearing) { return Doc.Disappearing; }
        if (Setting === AppChatSettingsRules.Star) { return Doc.Star; }
        if (Setting === AppChatSettingsRules.Pin) { return Doc.Pin; }
        if (Setting === AppChatSettingsRules.Read) { return Doc.Read; }
        if (Setting === AppChatSettingsRules.Block) { return Doc.Block; }
        if (Setting === AppChatSettingsRules.Clear) { return Doc.Clear; }
        if (Setting === AppChatSettingsRules.Delete) { return Doc.Delete; }
        return Doc.Archive;
    };

    /** The details section's fields, in order. */
    public static readonly InfoFields: string[] = ["about", "category", "description", "address", "hours", "website", "email", "timer"];

    /** [id, label, value] for each detail that has a value. ⛔ Never a field without one (nothing "not known"). */
    public static InfoRows = (Info: AppContactInfo | null): string[][] => {
        const Out: string[][] = [];
        if (getCoreUtils().KErrorMode === true || Info === null) { return Out; }
        try {
            if (Info.About.length > 0) { Out.push(["about", "About", Info.About]); }
            if (Info.Category.length > 0) { Out.push(["category", "Business", Info.Category]); }
            if (Info.Description.length > 0) { Out.push(["description", "Description", Info.Description]); }
            if (Info.Address.length > 0) { Out.push(["address", "Address", Info.Address]); }
            if (Info.Hours.length > 0) { Out.push(["hours", "Hours", Info.Hours.join(", ")]); }
            if (Info.Website.length > 0) { Out.push(["website", "Website", Info.Website]); }
            if (Info.Email.length > 0) { Out.push(["email", "Email", Info.Email]); }
            if (Info.DefaultTimer > 0) { Out.push(["timer", "Their default timer", AppChatState.Words("disappearing", AppChatSettingsRules.TimerProp(Info.DefaultTimer), 0, false)]); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.InfoRows");
            return Out;
        }
    };
    /** DOCUMENTED EXCEPTION: pure - a measured timer value for the shared words. */
    private static TimerProp = (Seconds: number): AppChatProp => {
        const Out: AppChatProp = new AppChatProp(); Out.Value = Seconds; Out.Measured = true; return Out;
    };

    /** The end of a timed mute in the reader's own clock: "Tue 14 Oct, 18:30". */
    public static UntilWords = (Ms: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppStanding.LocalTime(Ms);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.UntilWords");
            return "";
        }
    };

    /**
     * ⛔ ALWAYS A VALUE. Notifications say "On", "Muted" or "Muted until <local time>" (`Until` words the end time); the
     * other three are `AppChatState`'s own words.
     */
    public static ValueWords = (Setting: string, Value: AppChatSettingValue, NowMs: number, Until: (Ms: number) => string = AppChatSettingsRules.UntilWords): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Setting !== AppChatSettingsRules.Mute) {
                const Prop: AppChatProp = new AppChatProp();
                Prop.Value = Value; Prop.Measured = true;
                return AppChatState.Words(Setting, Prop, NowMs, false);
            }
            if (Value === -1) { return "Muted"; }
            if (typeof Value === "number" && Value > 0 && (NowMs <= 0 || Value > NowMs)) {
                const When: string = Until(Value);
                if (When.length === 0) { return "Muted"; }
                return "Muted until " + When;
            }
            return "On";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.ValueWords");
            return "";
        }
    };

    /** Which preset is pressed for a value ("" = none: a timed mute, a timer WhatsApp set to another length). */
    public static ChoiceOf = (Setting: string, Value: AppChatSettingValue, NowMs: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof Value !== "number") { return ""; }
            if (Setting === AppChatSettingsRules.Mute) {
                if (Value === -1) { return "-1"; }
                if (Value > 0 && (NowMs <= 0 || Value > NowMs)) { return ""; }
                return "0";
            }
            for (const One of AppChatSettingsRules.TimerOptions) { if (One[0] === String(Value)) { return One[0]; } }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.ChoiceOf");
            return "";
        }
    };

    /** Does the service's value show the change the owner asked for? (A timed mute: any mute still running.) */
    public static Matches = (Setting: string, Value: AppChatSettingValue, Wanted: AppChatSettingValue, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Setting !== AppChatSettingsRules.Mute) { return Value === Wanted; }
            if (Wanted === -1) { return Value === -1; }
            const Running: boolean = typeof Value === "number" && Value > 0 && (NowMs <= 0 || Value > NowMs);
            if (Wanted === 0) { return Value !== -1 && Running === false; }
            return Running === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.Matches");
            return false;
        }
    };

    /** Would choosing `Wanted` change anything? (Muting for a period again always does: it starts a new period.) */
    public static IsChange = (Setting: string, Value: AppChatSettingValue, Wanted: AppChatSettingValue, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Setting === AppChatSettingsRules.Mute && typeof Wanted === "number" && Wanted > 0) { return true; }
            if (AppChatSettingsRules.IsAction(Setting) === true) { return Wanted === true; }
            return AppChatSettingsRules.Matches(Setting, Value, Wanted, NowMs) === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.IsChange");
            return false;
        }
    };

    /** May the owner choose `Wanted` for this setting at all (the service's own list)? */
    public static Allowed = (Setting: string, Wanted: unknown): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppChatArchiveRules.ValueOk(Setting, Wanted, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.Allowed");
            return false;
        }
    };

    /** A pressed control's value as the wire wants it: a switch's boolean, a choice's whole number; null = not one. */
    public static ValueIn = (Setting: string, Text: string): AppChatSettingValue | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppChatSettingsRules.IsAction(Setting) === true) { if (Text === "true") { return true; } return null; }
            if (AppChatSettingsRules.IsSwitch(Setting) === true) {
                if (Text === "true") { return true; }
                if (Text === "false") { return false; }
                return null;
            }
            if (/^-?\d{1,8}$/.test(nz(Text)) === false) { return null; }
            const Value: number = parseInt(Text, 10);
            if (AppChatSettingsRules.Allowed(Setting, Value) === false) { return null; }
            return Value;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.ValueIn");
            return null;
        }
    };

    /** A row before the first answer: loading, never a value that was not read and never a word for "not known". */
    public static LoadingRow = (Setting: string): AppChatSettingRow => {
        const Out: AppChatSettingRow = new AppChatSettingRow();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Setting = Setting; Out.Name = AppChatSettingsRules.Name(Setting);
            Out.Loading = true; Out.Disabled = true;
            Out.Hint = AppChatState.LoadingWords;
            if (Setting === AppChatSettingsRules.Star) { Out.Hint = AppChatSettingsRules.StarLoadingWords; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.LoadingRow");
            return Out;
        }
    };

    /**
     * One row from the service's answer. `Wanted` (not null) = a change the owner chose that is still in flight
     * (`Confirming` false) or awaiting WhatsApp (`Confirming` true): the row shows IT, at once, and cannot be pressed again.
     */
    public static RowOf = (Setting: string, Doc: AppChatStateDoc, NowMs: number, Wanted: AppChatSettingValue | null = null,
        Confirming: boolean = false, Note: string = "", Until: (Ms: number) => string = AppChatSettingsRules.UntilWords): AppChatSettingRow => {
        const Out: AppChatSettingRow = new AppChatSettingRow();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            const Prop: AppChatProp = AppChatSettingsRules.PropOf(Doc, Setting);
            Out.Setting = Setting; Out.Name = AppChatSettingsRules.Name(Setting); Out.Note = nz(Note);
            let Shown: AppChatSettingValue = Prop.Value;
            if (Wanted !== null) { Shown = AppChatSettingsRules.ShownFor(Setting, Wanted, NowMs); }
            Out.Words = AppChatSettingsRules.ValueWords(Setting, Shown, NowMs, Until);
            // ⭐ The phone's own count: "3 unread" (a marked-unread chat with no count stays "Unread").
            if (Setting === AppChatSettingsRules.Read && Wanted === null && Shown === false && Doc.Unread > 0) { Out.Words = String(Doc.Unread) + " unread"; }
            if (Wanted !== null && Setting === AppChatSettingsRules.Mute && typeof Wanted === "number" && Wanted > 0) { Out.Words = "Muted"; }
            Out.On = Shown === true;
            Out.Choice = AppChatSettingsRules.ChoiceOf(Setting, Shown, NowMs);
            if (Wanted !== null && AppChatSettingsRules.IsSwitch(Setting) === false) { Out.Choice = String(Wanted); }
            Out.Source = AppChatState.Source(Setting, Prop);
            AppChatSettingsRules.Progress(Out, Doc, Prop, Wanted, Confirming);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.RowOf");
            return Out;
        }
    };

    /** DOCUMENTED EXCEPTION: pure - a chosen mute period is shown as "running" until the service gives its end time. */
    private static ShownFor = (Setting: string, Wanted: AppChatSettingValue, NowMs: number): AppChatSettingValue => {
        if (Setting === AppChatSettingsRules.Mute && typeof Wanted === "number" && Wanted > 0) { return Math.max(NowMs, 0) + Wanted * 1000; }
        return Wanted;
    };

    /** The row's in-progress words and whether it can be pressed. */
    private static Progress = (Out: AppChatSettingRow, Doc: AppChatStateDoc, Prop: AppChatProp, Wanted: AppChatSettingValue | null, Confirming: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Wanted !== null || Prop.Pending === true) {
                Out.Busy = true; Out.Disabled = true; Out.Source = "";
                Out.Hint = AppChatSettingsRules.SavingWords;
                if (Confirming === true || Wanted === null) { Out.Hint = AppChatSettingsRules.ConfirmingWords; }
                return;
            }
            if (Prop.Measured === false && Doc.Resolving === true && Out.Setting !== AppChatSettingsRules.Star) { Out.Hint = AppChatState.LoadingWords; }
            if (Prop.Can === true) { return; }
            Out.Disabled = true;
            Out.Why = Prop.Why;
            if (Out.Why.length === 0) { Out.Why = AppChatSettingsRules.CannotWhy(Out.Setting, Doc); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.Progress");
        }
    };

    /** A disabled control always says why - the service's sentence, else the one fact this page knows. */
    private static CannotWhy = (Setting: string, Doc: AppChatStateDoc): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Setting === AppChatSettingsRules.Star) { return "This star cannot be changed from this browser right now."; }
            if (Doc.Online !== true) { return "WhatsApp is disconnected. Reconnect before changing this chat."; }
            return "This cannot be changed from here right now. Change it in WhatsApp on your phone.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.CannotWhy");
            return "";
        }
    };

    /** Read one answer (a read's or a write's): the state document, and what a non-answer means. */
    public static Verdict = (Status: number, Text: string, ChatId: string): AppChatSettingsVerdict => {
        const Out: AppChatSettingsVerdict = new AppChatSettingsVerdict();
        if (getCoreUtils().KErrorMode === true) { Out.Lost = true; return Out; }
        try {
            Out.Doc = AppChatState.Parse(Status, Text, ChatId);
            if (Out.Doc.Ok === true) { return Out; }
            const Body: Record<string, unknown> | null = kJsonObject.ObjectOfJson(Text);
            const Gone: boolean = Status === 0 || Status >= 500;
            if (Gone === true) { Out.Lost = true; return Out; }
            if (Body === null || typeof Body.ok !== "boolean") { Out.Lost = true; return Out; }
            Out.Pending = Body.pending === true;
            const Said: string = typeof Body.why === "string" ? nz(Body.why) : "";
            if (Said.length > 0) { Out.Why = Said; }
            const Retry: unknown = Body.retry;
            if (typeof Retry === "number" && Number.isSafeInteger(Retry) === true && Retry >= 1) { Out.RetrySec = Math.min(Retry, AppChatSettingsRules.MaxRetrySec); }
            // An `ok: true` body about ANOTHER chat (or a broken one) is not an answer about this chat.
            if (Body.ok === true) { Out.Lost = true; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsRules.Verdict");
            Out.Lost = true;
            return Out;
        }
    };
}
